import type { ColDef } from 'ag-grid-community';
import mapValues from 'lodash/mapValues';
import { useCallback } from 'react';
import pick from 'lodash/pick';

import Config from '../../config';
import { CustomReportTypes } from '../../shared/customReports/constants';
import { useAddJob, useGetLastJobOfType } from '../../shared/jim/hooks';
import { localStorageService } from '../../utils/storageUtils';
import { getLocalStorageKey } from '../../shared/customReports/localStorage';
import { getColumnDefs } from '../../shared/customReports/helpers';
import {
  CustomReportPayload,
  ReportLocalStorageState,
} from '../../shared/customReports/types';
import {
  CustomReport as CustomReportQuery,
  CustomReport as CustomReportQueryData,
  CustomReportSet,
} from '@common/types/customReports';
import { jsonParseSafe, JsonValue } from '@common/utils/jsonUtils';
import { inProgressStatuses } from '../../shared/jim/constants';

const getColId = (colDef: ColDef) => colDef.colId ?? colDef.field!;

const getCustomReportJobColumnDefs = ({
  columns,
  columnState = [],
}: {
  columns: CustomReportQueryData['columns'],
  columnState?: JsonValue<ReportLocalStorageState['columnState']>,
}) => {
  const columnDefs = getColumnDefs({
    columns,
    types: {},
    filtersValues: {},
  }) as ColDef[];

  const columnsOrder = columnState.map(({ colId }) => colId?.toUpperCase());

  const getColIndex = (colId: string) => {
    const index = columnsOrder.indexOf(colId.toUpperCase());
    return index >= 0
      ? index
      : columnsOrder.length + columnDefs.findIndex(col => getColId(col) === colId);
  };

  return columnDefs.sort((colA, colB) =>
    getColIndex(getColId(colA)) - getColIndex(getColId(colB))
  ).map(colDef => {
    const colState = columnState.find(({ colId }) => getColId(colDef) === colId);

    return ({
      ...pick(colDef, ['headerName', 'field']),
      ...pick(colState ?? {}, ['hide', 'rowGroup', 'rowGroupIndex', 'sort', 'sortIndex']),
    });
  });
};


const getGridState = (reportId: string, reportSetId?: string) => {
  const reportSettings = localStorageService.getItem<ReportLocalStorageState>(getLocalStorageKey(
    reportId,
    reportSetId
  ));

  return {
    columnState: jsonParseSafe(reportSettings?.columnState, [])!,
    filterModel: jsonParseSafe(reportSettings?.filterModel, {})!,
  };
};

export type CustomReportExportFormat = 'xlsx' | 'csv';

const createSingleReportJobPayload = ({
  customReport,
  customReportPayload,
  exportFileName,
  exportFormat,
}: {
  customReport: CustomReportQuery
  customReportPayload: CustomReportPayload,
  exportFileName: string,
  exportFormat: CustomReportExportFormat,
}) => ({
  batchId: customReportPayload.reportId,
  name: exportFileName,
  displayName: exportFileName,
  exportFormat,
  reports: [
    {
      type: CustomReportTypes.REPORT,
      data: {
        reportId: customReportPayload.reportId,
        name: customReportPayload.name,
        params: customReportPayload.params,
        reportOptions: {
          columnDefinitions: getCustomReportJobColumnDefs({
            columns: customReport.columns,
            columnState: getGridState(customReportPayload.reportId).columnState,
          }),
          filters: mapValues(
            getGridState(customReportPayload.reportId).filterModel ?? {},
            ({ values }) => values
          ),
        },
      },
    },
  ],
});

const createReportSetJobPayload = ({
  customReportPayload,
  customReportSetReports,
  exportFileName,
  exportFormat,
}: {
  customReportPayload: CustomReportPayload,
  customReportSetReports: CustomReportQuery[],
  exportFileName: string,
  exportFormat: CustomReportExportFormat,
}) => ({
  batchId: customReportPayload.reportId,
  name: exportFileName,
  displayName: exportFileName,
  exportFormat,
  reports: [
    {
      type: CustomReportTypes.REPORT_SET,
      data: {
        reportId: customReportPayload.reportId,
        name: customReportPayload.name,
        queries: customReportPayload.queries.map(({ pillName, reportId, params }) => ({
          reportId,
          name: pillName,
          params,
          reportOptions: {
            columnDefinitions: getCustomReportJobColumnDefs({
              columns: customReportSetReports?.find(r => r.reportSetId === reportId)?.columns ?? [],
              columnState: getGridState(reportId, customReportPayload.reportId).columnState,
            }),
            filters: mapValues(
              getGridState(reportId, customReportPayload.reportId).filterModel ?? {},
              ({ values }) => values
            ),
          },
        })),
      },
    },
  ],
});

export const useCustomReportsJimExport = ({
  customReportSetReports,
  customReport,
  generateExportFileName,
}: {
  customReportSetReports?: CustomReportQueryData[];
  customReport: CustomReportQuery | CustomReportSet;
  generateExportFileName: (reportSetName: string, customReportQueryParams: CustomReportPayload['params']) => string;
}) => {
  const addJob = useAddJob({
    jobType: Config.jimJobs.customReportsExport.type,
    onSuccess: () => null,
    onError: () => null,
  });

  const exportJob = useGetLastJobOfType(Config.jimJobs.customReportsExport.type);

  const exportWithJim = useCallback((
    customReportPayload: CustomReportPayload,
    exportFormat: CustomReportExportFormat
  ) => {
    const exportFileName = generateExportFileName(customReportPayload.name, customReportPayload.params);

    if (customReportPayload?.customReportType === CustomReportTypes.REPORT_SET) {
      return addJob(createReportSetJobPayload({
        customReportPayload,
        customReportSetReports: customReportSetReports ?? [],
        exportFileName,
        exportFormat,
      }));
    }

    if (customReportPayload?.customReportType === CustomReportTypes.REPORT
      && customReport.customReportType === CustomReportTypes.REPORT) {
      addJob(createSingleReportJobPayload({
        customReportPayload,
        customReport,
        exportFileName,
        exportFormat,
      }));
    }
  }, [addJob, customReport, customReportSetReports, generateExportFileName]);

  return {
    exportWithJim,
    isExportRunning: !!(exportJob && inProgressStatuses.includes(exportJob?.status)),
  };
};
